
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function FreeAssessmentPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    industry: '',
    currentRevenue: '',
    biggestChallenge: '',
    goals: '',
    timeline: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 2000);
  };

  if (isSubmitted) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-2xl shadow-lg p-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  Assessment Request Received!
                </h1>
                <p className="text-lg text-gray-600 mb-8">
                  Thank you for your interest in our free growth assessment. We'll review your information and contact you within 24 hours to schedule your consultation.
                </p>
                <div className="bg-[#2BB6A3]/10 rounded-lg p-6 mb-8">
                  <h3 className="font-semibold text-gray-900 mb-3">What happens next:</h3>
                  <ul className="text-left space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      We'll review your business information
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      Schedule a 30-minute consultation call
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      Prepare a custom growth roadmap
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      Present actionable recommendations
                    </li>
                  </ul>
                </div>
                <button
                  onClick={() => window.location.href = '/'}
                  className="bg-[#2BB6A3] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#239B8A] transition-colors whitespace-nowrap"
                >
                  Return to Homepage
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Get Your Free Growth Assessment
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover exactly how to build predictable revenue systems for your service-based business. 
                Our 30-minute assessment will identify your biggest growth opportunities and provide a custom roadmap.
              </p>
            </div>

            {/* Benefits Section */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-[#2BB6A3]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-search-line text-2xl text-[#2BB6A3]"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Revenue Analysis</h3>
                <p className="text-gray-600">We'll analyze your current revenue streams and identify untapped opportunities.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-[#2BB6A3]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-roadmap-line text-2xl text-[#2BB6A3]"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Custom Roadmap</h3>
                <p className="text-gray-600">Get a personalized 90-day action plan to scale your business systematically.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-[#2BB6A3]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-team-line text-2xl text-[#2BB6A3]"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Guidance</h3>
                <p className="text-gray-600">Work directly with our team who has helped 75+ companies scale predictably.</p>
              </div>
            </div>

            {/* Assessment Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Business Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                      Website URL
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://yourwebsite.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                      Industry *
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      required
                      value={formData.industry}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent pr-8"
                    >
                      <option value="">Select your industry</option>
                      <option value="consulting">Consulting</option>
                      <option value="marketing">Marketing & Advertising</option>
                      <option value="technology">Technology Services</option>
                      <option value="professional-services">Professional Services</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="finance">Finance & Accounting</option>
                      <option value="real-estate">Real Estate</option>
                      <option value="education">Education & Training</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="currentRevenue" className="block text-sm font-medium text-gray-700 mb-2">
                      Current Annual Revenue *
                    </label>
                    <select
                      id="currentRevenue"
                      name="currentRevenue"
                      required
                      value={formData.currentRevenue}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent pr-8"
                    >
                      <option value="">Select revenue range</option>
                      <option value="under-100k">Under $100K</option>
                      <option value="100k-500k">$100K - $500K</option>
                      <option value="500k-1m">$500K - $1M</option>
                      <option value="1m-5m">$1M - $5M</option>
                      <option value="5m-plus">$5M+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="biggestChallenge" className="block text-sm font-medium text-gray-700 mb-2">
                    What's your biggest growth challenge? *
                  </label>
                  <textarea
                    id="biggestChallenge"
                    name="biggestChallenge"
                    required
                    rows={4}
                    value={formData.biggestChallenge}
                    onChange={handleInputChange}
                    placeholder="Describe your main challenge in growing your business..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent resize-none"
                    maxLength={500}
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {formData.biggestChallenge.length}/500 characters
                  </div>
                </div>

                <div>
                  <label htmlFor="goals" className="block text-sm font-medium text-gray-700 mb-2">
                    What are your revenue goals for the next 12 months? *
                  </label>
                  <textarea
                    id="goals"
                    name="goals"
                    required
                    rows={3}
                    value={formData.goals}
                    onChange={handleInputChange}
                    placeholder="Describe your revenue and growth goals..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent resize-none"
                    maxLength={500}
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {formData.goals.length}/500 characters
                  </div>
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                    When would you like to start implementing growth systems? *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    required
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent pr-8"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediately">Immediately</option>
                    <option value="within-30-days">Within 30 days</option>
                    <option value="within-90-days">Within 90 days</option>
                    <option value="within-6-months">Within 6 months</option>
                    <option value="just-exploring">Just exploring options</option>
                  </select>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">What you'll receive:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      30-minute consultation with our growth experts
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      Custom growth roadmap for your business
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      Actionable recommendations you can implement immediately
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-[#2BB6A3] mr-3"></i>
                      No obligation - completely free assessment
                    </li>
                  </ul>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2BB6A3] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#239B8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting Assessment...
                    </>
                  ) : (
                    <>
                      Get My Free Assessment
                      <i className="ri-arrow-right-line ml-2"></i>
                    </>
                  )}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting this form, you agree to receive communications from SparkBoost Labs. 
                  We respect your privacy and will never share your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
